import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Authentication is carried exclusively by the backend-issued `token`
// httpOnly + Secure + SameSite=Lax cookie. `withCredentials: true` is the
// browser-side opt-in that tells fetch/XHR to actually send + accept that
// cookie on cross-origin-ish requests. No JWT is ever read from JS.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------- Silent refresh interceptor ----------
// Goal: a panelist running an 8-hour qualitative interview must NEVER see a
// "session expired, log in again" modal mid-conversation. The short-lived
// access token (8h) is paired with a long-lived `refresh_token` cookie (7d).
// On any 401 we transparently call /auth/refresh, then replay the original
// request exactly once.
let refreshInFlight = null;       // dedup concurrent refreshes
let onAuthFailure = null;         // AuthContext registers a cleanup callback

export function registerAuthFailureHandler(cb) { onAuthFailure = cb; }

// Endpoints that must NEVER trigger the silent-refresh dance — otherwise
// we'd loop forever (e.g. /auth/refresh itself returning 401).
const NO_REFRESH_PATHS = ["/auth/refresh", "/auth/login", "/auth/logout"];

function isNoRefreshPath(url) {
  if (!url) return false;
  return NO_REFRESH_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config) return Promise.reject(error);
    if (isNoRefreshPath(config.url) || config._retriedAfterRefresh) {
      return Promise.reject(error);
    }
    try {
      // Dedup: every concurrent 401 awaits the SAME refresh promise — the
      // backend will reject the second concurrent rotation otherwise (because
      // we rotate the JTI on success).
      refreshInFlight = refreshInFlight || api.post("/auth/refresh");
      await refreshInFlight;
      refreshInFlight = null;
      config._retriedAfterRefresh = true;
      return api(config);
    } catch (refreshErr) {
      refreshInFlight = null;
      // Refresh failed → session is genuinely dead. Notify AuthContext so it
      // can clear `user` state and the UI can decide what to do next.
      if (onAuthFailure) {
        try { onAuthFailure(); } catch { /* never let the cleanup throw */ }
      }
      return Promise.reject(refreshErr);
    }
  },
);

export function formatApiErrorDetail(detail) {
  // Return null when there is nothing meaningful to show so callers can
  // fall back to their own message (`err.message`, a validation string,
  // etc.). Returning the generic "Something went wrong…" here would
  // short-circuit the caller's `||` fallback and hide the real cause.
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
